import React, { useState } from 'react';
import { Autocomplete, TextField, CircularProgress, Checkbox, ListItemText } from '@mui/material';
import { userService } from '../../services/users/usersServices';
import { useTenant } from '../../context/TenantContext';
import { useQuery } from '@tanstack/react-query';

interface UserOption {
  id: string;
  name: string;
  email?: string;
}

interface UserSelectProps {
  value: UserOption | UserOption[] | null;
  onChange: (value: UserOption | UserOption[] | null) => void;
  label?: string;
  multiple?: boolean;
  tenantId?: string;
  typeFlag?: string | string[];
  filter?: Record<string, any>; 
  disabled?: boolean;
  placeholder?: string;
  excludeIds?: string[];
}

function useDebouncedValue<T>(value: T, delay = 400) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

export function UserSelect({
  value,
  onChange,
  label = 'Usuário',
  multiple = false,
  tenantId,
  typeFlag,
  disabled = false,
  placeholder = 'Buscar usuário...',
  filter,
  excludeIds = [],
}: UserSelectProps) {
  const { selectedTenant } = useTenant();
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebouncedValue(inputValue, 400);

  const { data: options = [], isFetching } = useQuery({
    queryKey: [
      'users',
      tenantId || selectedTenant,
      Array.isArray(typeFlag) ? typeFlag.join(',') : typeFlag,
      debouncedInput,
      filter,
    ],
    queryFn: async () => {
      const params = {
        tenantId: tenantId || selectedTenant,
        typeFlag: Array.isArray(typeFlag) ? typeFlag.join(',') : typeFlag,
        search: debouncedInput,
        limit: 1000,
        ...(filter || {}),
      };
      const res = await userService.list(params);
      return res.data?.users|| [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const filteredOptions = excludeIds.length > 0
    ? options.filter(option => !excludeIds.includes(option.id))
    : options;

  return (
    <Autocomplete
      multiple={multiple}
      options={filteredOptions}
      value={value || (multiple ? [] : null)}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={option => `${option.name}`}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      loading={isFetching}
      disabled={disabled}
      filterSelectedOptions
      onInputChange={(_, newInput) => setInputValue(newInput)}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          margin="normal"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          {multiple && <Checkbox checked={selected} style={{ marginRight: 8 }} />}
          <ListItemText
            primary={option.name}

          />
        </li>
      )}
      noOptionsText="Nenhum usuário encontrado"
    />
  );
}